import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Ghost, Users, Calendar, Wallet, ScrollText, Star, Gift, Zap, Copy, Upload, Check } from 'lucide-react';
import { toast } from 'sonner';
import { FALLBACK_CITIES } from '../lib/cities';

const TABS = [
  { k:'overview', l:'Inbox', icon: Ghost },
  { k:'bookings', l:'Bookings', icon: Calendar },
  { k:'hosts', l:'Host Apps', icon: Users },
  { k:'profiles', l:'Profiles', icon: Users },
  { k:'team', l:'Team', icon: Users },
  { k:'reviews', l:'Reviews', icon: Star },
  { k:'payouts', l:'Payouts', icon: Wallet },
  { k:'blog', l:'Blog', icon: ScrollText },
  { k:'gift', l:'Gift Cards', icon: Gift },
  { k:'integrations', l:'Integrations', icon: Zap },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState('overview');
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Ghost size={28} className="text-[#f97316]"/>
        <h1 className="text-3xl font-heading font-semibold">Ghostie HQ — Admin</h1>
        <Link to="/team/content" className="btn-primary text-sm ml-auto">
          <ScrollText size={14}/> Content Engine
        </Link>
      </div>
      <div className="flex flex-wrap gap-2 mb-8 border-b border-[#3f2e1e] pb-3">
        {TABS.map(t => (
          <button key={t.k} onClick={()=>setTab(t.k)} className={`chip ${tab===t.k?'active':''}`} data-testid={`admin-tab-${t.k}`}>
            <t.icon size={14} className="inline mr-1"/> {t.l}
          </button>
        ))}
      </div>
      {tab==='overview' && <Overview/>}
      {tab==='bookings' && <Bookings/>}
      {tab==='hosts' && <HostApps/>}
      {tab==='profiles' && <Profiles/>}
      {tab==='team' && <TeamMembers/>}
      {tab==='reviews' && <Reviews/>}
      {tab==='payouts' && <Payouts/>}
      {tab==='blog' && <BlogCMS/>}
      {tab==='gift' && <GiftCardsAdmin/>}
      {tab==='integrations' && <Integrations/>}
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  const load = () => api.get('/admin/overview').then(r=>setD(r.data));
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, []);
  if (!d) return <div className="text-[#a3a3a3]">Loading...</div>;

  const items = [
    ['🟧 New bookings', d.paid_pending, 'paid_pending_match'],
    ['📨 Host applications open', d.applications_open],
    ['🧙 Host vettings', d.host_apps_pending],
    ['📅 Upcoming confirmed', d.upcoming],
    ['🌟 Awaiting review', d.completed_await_review],
    ['💰 This month payout', `€${d.monthly_payout_total}`],
  ];
  return (
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
      <div>
        <h3 className="font-heading text-xl mb-3">📥 Bookings needing attention</h3>
        {d.inbox.length === 0 ? (
          <div className="magical-card p-6 text-[#a3a3a3] text-sm">Nothing in the inbox. ✨</div>
        ) : (
          <div className="space-y-2">
            {d.inbox.map(b => (
              <div key={b.booking_id} className="magical-card p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="badge-cozy text-[10px]">{b.source}</span>
                    <span className="text-xs text-[#a3a3a3]">{new Date(b.created_at).toLocaleString()}</span>
                  </div>
                  <div className="font-heading">{b.customer_name} · {b.city_id || b.custom_city_name}</div>
                  <div className="text-xs text-[#a3a3a3]">{b.date} {b.start_time} · {b.duration_hours}h · {b.number_of_people} ppl · €{b.total_paid}</div>
                </div>
                <span className={`badge-cozy text-[10px] ${b.status==='paid_pending_match'?'!text-[#f97316] !border-[#f97316]':''}`}>{b.status.replace(/_/g,' ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading text-xl mb-3">📊 Snapshot</h3>
        <div className="grid grid-cols-2 gap-3 mb-6">
          {items.map(([l,v])=>(
            <div key={l} className="stat-card">
              <div className="text-[11px] uppercase font-accent text-[#f97316] tracking-widest">{l}</div>
              <div className="text-2xl font-heading font-semibold text-[#f97316] mt-1">{v}</div>
            </div>
          ))}
        </div>
        <h3 className="font-heading text-xl mb-3">🌐 Sources this month</h3>
        <div className="magical-card p-4 space-y-2 text-sm">
          {(d.sources_month || []).length === 0 && <div className="text-[#a3a3a3]">No bookings yet this month.</div>}
          {(d.sources_month || []).map(s => (
            <div key={s._id} className="flex justify-between">
              <span className="capitalize">{s._id}</span>
              <span><b className="text-[#f97316]">{s.count}</b> · €{s.revenue?.toFixed(0)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Integrations() {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/admin/webhook-info').then(r=>setInfo(r.data)); }, []);

  const copyUrl = () => {
    navigator.clipboard.writeText(info?.url || '');
    setCopied(true); setTimeout(()=>setCopied(false), 1500);
  };

  const parseCsv = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h=>h.trim());
    return lines.slice(1).map(line => {
      const cells = line.split(',').map(c=>c.trim());
      const row = {};
      headers.forEach((h,i)=>{ row[h] = cells[i] || ''; });
      return row;
    });
  };

  const upload = async () => {
    const rows = parseCsv(csv);
    if (rows.length === 0) { toast.error('No rows parsed'); return; }
    setBusy(true);
    try {
      const r = await api.post('/admin/bookings/csv-import', { rows });
      setResult(r.data);
      toast.success(`Imported ${r.data.created} bookings`);
    } catch (e) {
      toast.error('Import failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="magical-card p-6">
        <div className="flex items-center gap-2 mb-2"><Zap size={18} className="text-[#f97316]"/><h3 className="font-heading text-xl">OTA Webhook (Zapier / Make.com)</h3></div>
        <p className="text-sm text-[#a3a3a3] mb-4">Connect GetYourGuide, TripAdvisor, Viator and Airbnb Experiences to Ghostie. Use Zapier or Make.com to forward each booking confirmation email → parse the fields → POST to this URL. Bookings appear instantly in your Inbox.</p>
        <div className="bg-[#0a0a0a] border border-[#3f2e1e] rounded-xl p-3 font-mono text-xs break-all flex items-start gap-3 mb-3">
          <span className="flex-1">{info?.url || 'Loading…'}</span>
          <button onClick={copyUrl} className="chip text-[10px] py-1 shrink-0" data-testid="copy-webhook">{copied ? <><Check size={10}/> Copied</> : <><Copy size={10}/> Copy</>}</button>
        </div>
        <div className="text-xs text-[#a3a3a3] mb-2">Required JSON body (POST):</div>
        <pre className="bg-[#0a0a0a] border border-[#3f2e1e] rounded-xl p-3 text-[11px] overflow-x-auto">{JSON.stringify(info?.sample_payload || {}, null, 2)}</pre>
        <div className="mt-4 text-xs text-[#a3a3a3] space-y-1">
          <div><b className="text-[#f97316]">Setup with Zapier:</b> 1. New trigger: Gmail → "New email matching search" (filter for "GetYourGuide booking confirmation"). 2. Use the email parser to extract fields. 3. Action: Webhooks by Zapier → POST → paste URL above. <a href="https://zapier.com/apps/webhook" target="_blank" rel="noreferrer" className="text-[#f97316]">Docs ↗</a></div>
          <div><b className="text-[#f97316]">Setup with Make.com:</b> Email module → Text parser → HTTP POST to URL above. Free tier: 1,000 ops/month.</div>
        </div>
      </div>

      <div className="magical-card p-6">
        <div className="flex items-center gap-2 mb-2"><Upload size={18} className="text-[#f97316]"/><h3 className="font-heading text-xl">CSV bulk import</h3></div>
        <p className="text-sm text-[#a3a3a3] mb-3">Paste rows from a spreadsheet. Required headers: <code className="text-[#f97316]">source,customer_name,city_name,date,start_time,duration_hours,number_of_people,paid_amount</code> (also: customer_email, customer_phone, currency, special_requests, external_reference).</p>
        <textarea rows={8} value={csv} onChange={e=>setCsv(e.target.value)} placeholder="source,customer_name,city_name,date,start_time,duration_hours,number_of_people,paid_amount,customer_email&#10;getyourguide,Maya Smith,London,2026-04-15,19:00,3,2,300,maya@example.com" className="input-cozy font-mono text-xs" data-testid="csv-textarea"/>
        <button onClick={upload} disabled={busy} className="btn-primary mt-3" data-testid="csv-import-btn"><Upload size={14}/> {busy?'Importing…':'Import'}</button>
        {result && (
          <div className="mt-4 text-sm">
            <div className="text-[#f97316]">✓ Created {result.created} bookings.</div>
            {result.errors.length > 0 && (
              <div className="text-red-400 mt-1">Errors: {result.errors.map(e => `row ${e.row}: ${e.error}`).join('; ')}</div>
            )}
          </div>
        )}
      </div>

      <div className="magical-card p-6">
        <div className="flex items-center gap-2 mb-2">📧<h3 className="font-heading text-xl">Email integration (Resend)</h3></div>
        <p className="text-sm text-[#a3a3a3]">Add <code className="text-[#f97316]">RESEND_API_KEY=re_...</code> to <code>/app/backend/.env</code> then restart backend. All transactional emails will send via Resend with a branded HTML template. Until then, they're queued in the <code>email_log</code> collection.</p>
      </div>
    </div>
  );
}

function Bookings() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [apps, setApps] = useState([]);
  const [showExt, setShowExt] = useState(false);
  const load = () => api.get('/admin/bookings', { params: filter?{status:filter}:{} }).then(r=>setList(r.data));
  useEffect(() => { load(); }, [filter]);
  const openBooking = async (b) => {
    setSelected(b);
    const r = await api.get(`/admin/bookings/${b.booking_id}/applications`);
    setApps(r.data);
  };
  const assign = async (hid) => {
    await api.post('/admin/assign-host', { booking_id: selected.booking_id, host_user_id: hid });
    toast.success('Host assigned'); load(); openBooking(selected);
  };
  const setStatus = async (status) => {
    await api.post('/admin/booking-status', { booking_id: selected.booking_id, status });
    toast.success('Updated'); load();
  };
  const markComplete = async () => {
    await api.post('/admin/booking-complete', { booking_id: selected.booking_id });
    toast.success('Marked completed'); load();
  };
  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        <select value={filter} onChange={e=>setFilter(e.target.value)} className="input-cozy max-w-xs" data-testid="admin-bookings-filter">
          <option value="">All statuses</option>
          {['paid_pending_match','external_paid_pending_match','host_applications_open','confirmed_chat_open','completed','review_requested','closed','cancelled_by_customer','cancelled_by_admin','refunded'].map(s=><option key={s}>{s}</option>)}
        </select>
        <button onClick={()=>setShowExt(!showExt)} className="btn-secondary text-sm" data-testid="admin-create-ext">+ External booking</button>
      </div>
      {showExt && <ExternalBookingForm onDone={()=>{setShowExt(false); load();}}/>}
      <div className="grid md:grid-cols-[1.2fr_1fr] gap-6">
        <div className="space-y-2 max-h-[70vh] overflow-y-auto">
          {list.map(b => (
            <button key={b.booking_id} onClick={()=>openBooking(b)} className="magical-card p-4 text-left block w-full">
              <div className="flex justify-between gap-2">
                <div>
                  <div className="font-semibold">{b.customer_name} · {b.date}</div>
                  <div className="text-xs text-[#a3a3a3]">{b.city_id} · {b.duration_hours}h · €{b.total_paid} · {b.source}</div>
                </div>
                <div className="badge-cozy text-[10px] self-start">{b.status.replace(/_/g,' ')}</div>
              </div>
            </button>
          ))}
        </div>
        {selected && (
          <div className="magical-card p-5 sticky top-24 h-fit">
            <h3 className="font-heading text-lg mb-2">{selected.customer_name}</h3>
            <div className="text-xs text-[#a3a3a3] mb-3">{selected.customer_email} · {selected.customer_phone}</div>
            <div className="badge-cozy text-[10px] mb-3">Booking code: {selected.booking_id}</div>
            <div className="text-sm space-y-1 mb-4">
              <div>Date: {selected.date} {selected.start_time} ({selected.duration_hours}h)</div>
              <div>People: {selected.number_of_people} · Lang: {selected.preferred_language}</div>
              <div>City: {selected.city_id}{selected.custom_city_name?` (${selected.custom_city_name})`:''}</div>
              <div>Total: €{selected.total_paid}</div>
              <div>Tags: {(selected.interest_tags||[]).join(', ')}</div>
              {selected.special_requests && <div className="text-[#a3a3a3] italic">"{selected.special_requests}"</div>}
              {selected.emergency_contact_name && <div>Emergency: {selected.emergency_contact_name} · {selected.emergency_contact_phone}</div>}
            </div>
            <div className="text-xs font-accent uppercase tracking-widest text-[#f97316] mb-2">Host applications</div>
            <div className="space-y-2 mb-4">
              {apps.length===0 && <div className="text-xs text-[#a3a3a3]">None yet.</div>}
              {apps.map(a => (
                <div key={a.app_id} className="border border-[#3f2e1e] rounded-xl p-3 text-sm">
                  <div className="font-semibold">{a.host_user?.name} <span className="text-xs text-[#a3a3a3]">· {a.status}</span></div>
                  <div className="text-xs text-[#a3a3a3]">Langs: {a.host_profile?.languages?.join(', ')}</div>
                  {a.application_message && <div className="text-xs italic text-[#a3a3a3]">"{a.application_message}"</div>}
                  {a.status==='applied' && (
                    <button onClick={()=>assign(a.host_user_id)} className="btn-primary text-xs mt-2 py-1 px-3" data-testid={`assign-${a.host_user_id}`}>Assign</button>
                  )}
                </div>
              ))}
            </div>
            <div className="text-xs font-accent uppercase tracking-widest text-[#f97316] mb-2">Actions</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={markComplete} className="chip">Mark completed</button>
              <button onClick={()=>setStatus('credit_offered')} className="chip">Credit offered</button>
              <button onClick={()=>setStatus('refund_required')} className="chip">Need refund</button>
              <button onClick={()=>setStatus('refunded')} className="chip">Refunded</button>
              <button onClick={()=>setStatus('cancelled_by_admin')} className="chip">Cancel</button>
            </div>
            <NotesBox targetType="booking" targetId={selected.booking_id}/>
          </div>
        )}
      </div>
    </div>
  );
}

function ExternalBookingForm({ onDone }) {
  const [cities, setCities] = useState([]);
  const [f, setF] = useState({
    source:'getyourguide', external_reference:'', customer_name:'', customer_email:'', customer_phone:'',
    city_id:'', date:'', start_time:'19:00', duration_hours:2, number_of_people:2,
    paid_amount:0, currency:'EUR', customer_language:'English', special_requests:'',
    emergency_contact_name:'', emergency_contact_phone:'',
  });
  useEffect(() => { api.get('/cities').then(r=>setCities(r.data)); }, []);
  const submit = async () => {
    try { await api.post('/admin/external-booking', f); toast.success('External booking created'); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="magical-card p-5 mb-6 grid grid-cols-2 gap-3">
      <select className="input-cozy" value={f.source} onChange={e=>setF(s=>({...s,source:e.target.value}))}><option>getyourguide</option><option>viator</option><option>airbnb</option><option>manual</option><option>other</option></select>
      <input className="input-cozy" placeholder="External ref" value={f.external_reference} onChange={e=>setF(s=>({...s,external_reference:e.target.value}))}/>
      <input className="input-cozy" placeholder="Customer name" value={f.customer_name} onChange={e=>setF(s=>({...s,customer_name:e.target.value}))}/>
      <input className="input-cozy" placeholder="Customer email" value={f.customer_email} onChange={e=>setF(s=>({...s,customer_email:e.target.value}))}/>
      <input className="input-cozy" placeholder="Customer phone" value={f.customer_phone} onChange={e=>setF(s=>({...s,customer_phone:e.target.value}))}/>
      <select className="input-cozy" value={f.city_id} onChange={e=>setF(s=>({...s,city_id:e.target.value}))}><option value="">City...</option>{cities.map(c=><option key={c.city_id} value={c.city_id}>{c.name}</option>)}</select>
      <input className="input-cozy" type="date" value={f.date} onChange={e=>setF(s=>({...s,date:e.target.value}))}/>
      <input className="input-cozy" type="time" value={f.start_time} onChange={e=>setF(s=>({...s,start_time:e.target.value}))}/>
      <input className="input-cozy" type="number" min={2} max={6} value={f.duration_hours} onChange={e=>setF(s=>({...s,duration_hours:+e.target.value}))}/>
      <input className="input-cozy" type="number" min={1} max={6} value={f.number_of_people} onChange={e=>setF(s=>({...s,number_of_people:+e.target.value}))}/>
      <input className="input-cozy" type="number" placeholder="Paid amount" value={f.paid_amount} onChange={e=>setF(s=>({...s,paid_amount:+e.target.value}))}/>
      <input className="input-cozy" value={f.currency} onChange={e=>setF(s=>({...s,currency:e.target.value}))}/>
      <textarea className="input-cozy col-span-2" placeholder="Special requests" value={f.special_requests} onChange={e=>setF(s=>({...s,special_requests:e.target.value}))}/>
      <button onClick={submit} className="btn-primary col-span-2" data-testid="ext-submit">Create external booking</button>
    </div>
  );
}

function HostApps() {
  const [list, setList] = useState([]);
  const load = () => api.get('/admin/host-applications').then(r=>setList(r.data));
  useEffect(() => { load(); }, []);
  const action = async (host_user_id, status, field=null) => {
    await api.post('/admin/host-status', { host_user_id, status, field });
    toast.success('Updated'); load();
  };
  return (
    <div className="space-y-3">
      {list.map(h => (
        <div key={h.host_profile_id} className="magical-card p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="font-heading text-lg">{h.user?.name} <span className="text-xs text-[#a3a3a3]">· {h.user?.email}</span></div>
              <div className="text-xs text-[#a3a3a3]">{h.city_id} · {(h.languages||[]).join(', ')}</div>
              <p className="text-sm mt-2 max-w-2xl">{h.bio}</p>
              <p className="text-sm text-[#a3a3a3] mt-1">{h.topic_expertise}</p>
            </div>
            <div className="badge-cozy">{h.status.replace(/_/g,' ')}</div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button onClick={()=>action(h.user_id,'interview_pending')} className="chip">→ Interview pending</button>
            <button onClick={()=>action(h.user_id,'interview_pending','interview_completed')} className="chip">✓ Interview done</button>
            <button onClick={()=>action(h.user_id,'contract_pending','contract_signed')} className="chip">✓ Contract signed</button>
            <button onClick={()=>action(h.user_id,'identity_check_pending','identity_verified')} className="chip">✓ ID verified</button>
            <button onClick={()=>action(h.user_id,'approved_active')} className="chip" data-testid={`approve-${h.user_id}`}>✓ Approve active</button>
            <button onClick={()=>action(h.user_id,'paused')} className="chip">Pause</button>
            <button onClick={()=>action(h.user_id,'rejected')} className="chip">Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function NotesBox({ targetType, targetId }) {
  const [notes, setNotes] = useState([]);
  const [note, setNote] = useState('');
  const load = () => api.get(`/admin/notes/${targetType}/${targetId}`).then(r=>setNotes(r.data)).catch(()=>setNotes([]));
  useEffect(() => { if (targetId) load(); }, [targetType, targetId]);
  const add = async () => {
    if (!note.trim()) return;
    await api.post('/admin/notes', { target_type: targetType, target_id: targetId, note });
    setNote('');
    load();
  };
  return (
    <div className="mt-3 border-t border-[#3f2e1e] pt-3">
      <div className="text-xs font-accent uppercase tracking-widest text-[#f97316] mb-2">Internal notes</div>
      <div className="space-y-2 mb-2">
        {notes.length === 0 && <div className="text-xs text-[#a3a3a3]">No notes yet.</div>}
        {notes.map(n => (
          <div key={n.note_id} className="bg-[#0a0a0a] border border-[#3f2e1e] rounded-xl p-2 text-xs">
            <div className="text-[#a3a3a3] mb-1">{n.created_by_name || 'Team'} · {new Date(n.created_at).toLocaleString()}</div>
            <div>{n.note}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input className="input-cozy text-xs" value={note} onChange={e=>setNote(e.target.value)} placeholder="Add private note..." />
        <button onClick={add} className="btn-secondary text-xs py-2 px-3">Add</button>
      </div>
    </div>
  );
}

function Profiles() {
  const [travellers, setTravellers] = useState([]);
  const [hosts, setHosts] = useState([]);
  const load = async () => {
    const [t, h] = await Promise.all([
      api.get('/admin/users', { params: { role: 'customer' } }).then(r=>r.data),
      api.get('/admin/host-applications').then(r=>r.data),
    ]);
    setTravellers(t);
    setHosts(h);
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <h3 className="font-heading text-2xl mb-4">Ghostie profiles</h3>
        <div className="space-y-3">
          {hosts.map(h => (
            <div key={h.host_profile_id} className="magical-card p-4">
              <div className="font-semibold">{h.user?.name || h.full_name || h.user_id}</div>
              <div className="text-xs text-[#a3a3a3]">{h.user?.email} · {h.city_id} · {h.status}</div>
              <p className="text-sm text-[#a3a3a3] mt-2 line-clamp-2">{h.bio}</p>
              <NotesBox targetType="host" targetId={h.user_id}/>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-heading text-2xl mb-4">Traveller profiles</h3>
        <div className="space-y-3">
          {travellers.map(u => (
            <div key={u.user_id} className="magical-card p-4">
              <div className="font-semibold">{u.name || 'Unnamed traveller'}</div>
              <div className="text-xs text-[#a3a3a3]">{u.email} · joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'unknown'}</div>
              <NotesBox targetType="traveller" targetId={u.user_id}/>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TeamMembers() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name:'', email:'', password:'' });
  const load = () => api.get('/admin/users', { params: { role: 'admin' } }).then(r=>setList(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    await api.post('/admin/team-members', f);
    toast.success('Team member created');
    setF({ name:'', email:'', password:'' });
    load();
  };
  return (
    <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
      <form onSubmit={create} className="magical-card p-5 space-y-3 h-fit">
        <h3 className="font-heading text-2xl">Create team member</h3>
        <input required className="input-cozy" placeholder="Name" value={f.name} onChange={e=>setF(s=>({...s,name:e.target.value}))}/>
        <input required type="email" className="input-cozy" placeholder="Email" value={f.email} onChange={e=>setF(s=>({...s,email:e.target.value}))}/>
        <input required minLength={8} type="password" className="input-cozy" placeholder="Temporary password" value={f.password} onChange={e=>setF(s=>({...s,password:e.target.value}))}/>
        <button className="btn-primary">Create access</button>
        <p className="text-xs text-[#a3a3a3]">Team members can login from the hidden <code>/team</code> page.</p>
      </form>
      <div>
        <h3 className="font-heading text-2xl mb-4">Current team</h3>
        <div className="space-y-3">
          {list.map(u => (
            <div key={u.user_id} className="magical-card p-4">
              <div className="font-semibold">{u.name}</div>
              <div className="text-xs text-[#a3a3a3]">{u.email} · {u.role}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Reviews() {
  const [list, setList] = useState([]);
  const load = () => api.get('/admin/reviews').then(r=>setList(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (id) => { await api.post(`/admin/reviews/${id}/approve`); toast.success('Approved'); load(); };
  return (
    <div className="space-y-3">
      {list.length===0 && <div className="text-[#a3a3a3]">No reviews yet.</div>}
      {list.map(r => (
        <div key={r.review_id} className="magical-card p-5">
          <div className="flex items-center justify-between">
            <div className="text-[#f97316]">{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</div>
            <div className="text-xs">{r.public_approved ? <span className="text-[#f97316]">Public</span> : <button onClick={()=>approve(r.review_id)} className="chip">Approve public</button>}</div>
          </div>
          <p className="text-sm mt-2">{r.text}</p>
          {r.bonus_applied && <div className="badge-cozy mt-2 text-[10px]">+€10 bonus accrued</div>}
        </div>
      ))}
    </div>
  );
}

function Payouts() {
  const [d, setD] = useState({ items:[], by_host:[] });
  const month = new Date().toISOString().slice(0,7);
  const load = () => api.get('/admin/payouts').then(r=>setD(r.data));
  useEffect(() => { load(); }, []);
  const markPaid = async (host_id) => { await api.post('/admin/payouts/mark-paid', { host_id, month }); toast.success('Marked paid'); load(); };
  return (
    <div className="space-y-4">
      {d.by_host.length===0 && <div className="text-[#a3a3a3]">No payouts yet.</div>}
      {d.by_host.map(h => (
        <div key={h.host_id} className="magical-card p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading text-lg">{h.host_name}</div>
            <div className="text-xs text-[#a3a3a3]">{h.host_email} · {h.hours} hours · {h.items.length} entries</div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-heading font-semibold text-[#f97316]">€{h.total}</div>
            <button onClick={()=>markPaid(h.host_id)} className="chip mt-2">Mark paid ({month})</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function BlogCMS() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ title:'', excerpt:'', body:'', cover_image:'', status:'published', city_tag:'', category_tags:'' });
  const load = () => api.get('/admin/blog').then(r=>setList(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await api.post('/admin/blog', { ...f, city_tags: f.city_tag ? [f.city_tag] : [], category_tags: f.category_tags.split(',').map(x=>x.trim()).filter(Boolean) });
      toast.success('Post created'); setF({title:'',excerpt:'',body:'',cover_image:'',status:'published',city_tag:'',category_tags:''}); load();
    } catch (e) { toast.error('Failed'); }
  };
  return (
    <div className="grid md:grid-cols-[1fr_1fr] gap-6">
      <div className="magical-card p-5 space-y-3">
        <h3 className="font-heading text-lg">New post</h3>
        <input className="input-cozy" placeholder="Title" value={f.title} onChange={e=>setF(s=>({...s,title:e.target.value}))}/>
        <input className="input-cozy" placeholder="Excerpt" value={f.excerpt} onChange={e=>setF(s=>({...s,excerpt:e.target.value}))}/>
        <textarea rows={6} className="input-cozy" placeholder="Body" value={f.body} onChange={e=>setF(s=>({...s,body:e.target.value}))}/>
        <input className="input-cozy" placeholder="Cover image URL" value={f.cover_image} onChange={e=>setF(s=>({...s,cover_image:e.target.value}))}/>
        <input className="input-cozy" placeholder="Internal categories (comma, not shown publicly)" value={f.category_tags} onChange={e=>setF(s=>({...s,category_tags:e.target.value}))}/>
        <select className="input-cozy" value={f.city_tag} onChange={e=>setF(s=>({...s,city_tag:e.target.value}))}>
          <option value="">No city / general post</option>
          {FALLBACK_CITIES.map(city => <option key={city.city_id} value={city.name}>{city.name}</option>)}
        </select>
        <select className="input-cozy" value={f.status} onChange={e=>setF(s=>({...s,status:e.target.value}))}><option>draft</option><option>published</option></select>
        <button onClick={create} className="btn-primary" data-testid="blog-create">Publish</button>
      </div>
      <div className="space-y-3">
        {list.map(p => (
          <div key={p.post_id} className="magical-card p-4">
            <div className="font-heading">{p.title}</div>
            <div className="text-xs text-[#a3a3a3]">{p.slug} · {p.status}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GiftCardsAdmin() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get('/admin/gift-cards').then(r=>setList(r.data)); }, []);
  return (
    <div className="space-y-2">
      {list.length===0 && <div className="text-[#a3a3a3]">No gift cards yet.</div>}
      {list.map(g => (
        <div key={g.gift_card_id} className="magical-card p-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[#f97316]">{g.code}</div>
            <div className="text-xs text-[#a3a3a3]">{g.purchaser_email} → {g.recipient_email || '—'}</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-xl">€{g.original_amount}</div>
            <div className="text-xs text-[#a3a3a3]">{g.status}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
